import { DocumentFile, Note } from '@/store/document'
import { IApi } from '@/store/spi/api'
import {
  handleError,
  hasError,
  tryCatch,
  unknownError,
} from '@repo/shared/api/helpers'
import {
  DocumentFileDTO,
  DocumentNoteDTO,
  DocumentType,
} from '@repo/shared/dtos/document'
import { DocumentsRoutes } from '@repo/shared/routes-documents'
import { isNoteDocument } from '@repo/shared/utils'
import { AxiosInstance } from 'axios'

type T = {
  app: IApi['documents']
}

export const apiDocuments: (api?: AxiosInstance) => T = (api): T => ({
  //
  // APP
  //
  app: {
    // TODO: get
    createNote: api
      ? async ({ beneficiaryId, organizationId, fields }) => {
          const res = await tryCatch(
            async () =>
              await api.post<DocumentsRoutes.createOne.Response>(
                DocumentsRoutes.createOne.call({
                  beneficiaryId,
                  organizationId,
                }),
                { fields } satisfies DocumentsRoutes.createOne.Request,
              ),
          )
          if (hasError(res)) return handleError(res)
          return toDocumentNote(res.document)
        }
      : (p) => {
          if (p.fields.content) {
            return Promise.resolve({
              content: p.fields.content,
              createdAt: new Date().getTime(),
              id: 'c3',
              type: DocumentType.NOTE,
              updatedAt: new Date().getTime(),
            })
          }
          return Promise.resolve(unknownError)
        },

    uploadOne: api
      ? async ({ beneficiaryId, organizationId, file }) => {
          const formData = new FormData()
          formData.append('file', file)
          const res = await tryCatch(
            async () =>
              await api.post<DocumentsRoutes.uploadOne.Response>(
                DocumentsRoutes.uploadOne.call({
                  beneficiaryId,
                  organizationId,
                }),
                formData,
                {
                  headers: {
                    'Content-Type': 'multipart/form-data',
                  },
                },
              ),
          )
          if (hasError(res)) return handleError(res)
          return toDocumentFile(res.document)
        }
      : () => {
          return Promise.resolve(unknownError)
        },

    getTemporaryUrl: api
      ? async ({ beneficiaryId, organizationId, documentId }) => {
          const res = await tryCatch(
            async () =>
              await api.get<DocumentsRoutes.getTemporaryUrl.Response>(
                DocumentsRoutes.getTemporaryUrl.call({
                  beneficiaryId,
                  organizationId,
                  documentId,
                }),
              ),
          )
          if (hasError(res)) return handleError(res)
          return {
            url: res.url,
          }
        }
      : () => {
          return Promise.resolve(unknownError)
        },

    updateOne: api
      ? async ({ beneficiaryId, organizationId, fields }) => {
          const res = await tryCatch(
            async () =>
              await api.patch<DocumentsRoutes.patchOne.Response>(
                DocumentsRoutes.patchOne.call({
                  beneficiaryId,
                  organizationId,
                }),
                { fields } satisfies DocumentsRoutes.patchOne.Request,
              ),
          )
          if (hasError(res)) return handleError(res)
          if (isNoteDocument(res.document)) return toDocumentNote(res.document)
          return toDocumentFile(res.document)
        }
      : (p) => {
          if ('content' in p.fields)
            return Promise.resolve({
              content: p.fields.content,
              createdAt: new Date().getTime(),
              id: p.fields.id,
              type: DocumentType.NOTE,
              updatedAt: new Date().getTime(),
            } satisfies Note)
          if ('title' in p.fields)
            return Promise.resolve({
              createdAt: new Date().getTime(),
              fileName: 'file.pdf',
              id: p.fields.id,
              size: 1234,
              title: p.fields.title,
              type: DocumentType.PDF,
              updatedAt: new Date().getTime(),
            } satisfies DocumentFile)
          return Promise.resolve(unknownError)
        },

    restoreOne: api
      ? async ({ beneficiaryId, organizationId, documentId }) => {
          const res = await tryCatch(
            async () =>
              await api.get<DocumentsRoutes.restoreOne.Response>(
                DocumentsRoutes.restoreOne.call({
                  beneficiaryId,
                  organizationId,
                  documentId,
                }),
              ),
          )
          if (hasError(res)) return handleError(res)
          if (isNoteDocument(res.document)) return toDocumentNote(res.document)
          return toDocumentFile(res.document)
        }
      : () => {
          return Promise.resolve({
            content: 'Restored note content',
            createdAt: new Date().getTime(),
            id: 'p1',
            type: DocumentType.NOTE,
            updatedAt: new Date().getTime(),
          } satisfies Note)
        },

    deleteOne: api
      ? async ({ beneficiaryId, documentId, organizationId }) => {
          const res = await tryCatch(
            async () =>
              await api.delete<DocumentsRoutes.deleteOne.Response>(
                DocumentsRoutes.deleteOne.call({
                  beneficiaryId,
                  documentId,
                  organizationId,
                }),
              ),
          )
          if (hasError(res)) return handleError(res)
          return res.success
        }
      : (p) => {
          if (
            p.beneficiaryId === 'benef1' &&
            p.organizationId === 'org1' &&
            p.documentId === 'c1'
          ) {
            return Promise.resolve(true)
          }
          return Promise.resolve(false)
        },
  },
})

const toDocumentFile = (document: DocumentFileDTO): DocumentFile => ({
  createdAt: document.createdAt,
  fileName: document.fileName,
  id: document.id,
  size: document.size,
  title: document.title,
  type: document.type,
  updatedAt: document.updatedAt,
})

const toDocumentNote = (document: DocumentNoteDTO): Note => ({
  content: document.content,
  createdAt: document.createdAt,
  id: document.id,
  type: document.type,
  updatedAt: document.updatedAt,
})
